import React, { useId } from 'react';

import AngleBracketsIcon from '@atlaskit/icon/core/angle-brackets';
import { BitbucketIcon } from '@atlaskit/logo/bitbucket/icon';
import { token } from '@atlaskit/tokens';

import { GitHubIcon } from '@atlassian/logo-third-party/github';

import { getDevDocsSourceProviderConfigFromValue } from './source-resolution/devDocsSourceProviders.ts';

export type DevDocsScmProviderIconSize = 'small' | 'medium' | 'large';

const DevDocsScmProviderBrandIcon = ({
	provider,
	size,
}: {
	readonly provider: 'bitbucket' | 'github';
	readonly size: DevDocsScmProviderIconSize;
}): React.JSX.Element => {
	if (provider === 'github') {
		switch (size) {
			case 'large':
				return <GitHubIcon label="" size="xlarge" testId="dev-docs-github-icon" />;
			case 'medium':
				return <GitHubIcon label="" size="large" testId="dev-docs-github-icon" />;
			default:
				return <GitHubIcon label="" size="small" testId="dev-docs-github-icon" />;
		}
	}

	switch (size) {
		case 'large':
			return <BitbucketIcon appearance="brand" label="" size="xlarge" />;
		case 'medium':
			return <BitbucketIcon appearance="brand" label="" size="large" />;
		default:
			return <BitbucketIcon appearance="brand" label="" size="small" />;
	}
};

export const DevDocsScmProviderIcon = ({
	provider,
}: {
	provider?: string | null;
}): React.JSX.Element => {
	const providerKey = getDevDocsSourceProviderConfigFromValue(provider)?.provider;

	switch (providerKey) {
		case 'github':
			return <GitHubIcon label="" size="xxsmall" testId="dev-docs-github-icon" />;
		case 'bitbucket':
			return <BitbucketIcon appearance="brand" label="" size="xsmall" />;
		default:
			return <AngleBracketsIcon label="" />;
	}
};

/**
 * Provider logo components own their presentation. First-party logos render
 * bare, while third-party logo components may include their own bordered tile.
 */
export const DevDocsScmProviderLogo = ({
	provider,
	size = 'small',
}: {
	readonly provider?: string | null;
	readonly size?: DevDocsScmProviderIconSize;
}): React.JSX.Element => {
	const providerKey = getDevDocsSourceProviderConfigFromValue(provider)?.provider;

	if (providerKey) {
		return <DevDocsScmProviderBrandIcon provider={providerKey} size={size} />;
	}

	return <DevDocsScmProviderIcon provider={provider} />;
};

export const DevDocsScmProviderSourceIcon = ({
	sourceUrl,
}: {
	sourceUrl: string;
}): React.JSX.Element => <DevDocsScmProviderIcon provider={sourceUrl} />;

export const DevDocsScmProviderButtonIcons = (): React.JSX.Element => {
	const id = useId().replace(/:/g, '');
	const githubMaskId = `devDocsScmProviderButtonIconsGithubMask-${id}`;
	const clipId = `devDocsScmProviderButtonIconsClip0-${id}`;

	return (
		<svg
			width="40"
			height="23"
			viewBox="0 0 40 23"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
			focusable="false"
		>
			<g mask={`url(#${githubMaskId})`}>
				<rect y="1.5" width="20" height="20" rx="5" fill="white" />
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M10.0001 5.5C6.68675 5.5 4 8.25424 4 11.6519C4 14.37 5.7192 16.676 8.1032 17.4894C8.40306 17.5464 8.51315 17.356 8.51315 17.1935C8.51315 17.0468 8.50759 16.5622 8.50501 16.0481C6.83579 16.4203 6.48356 15.3223 6.48356 15.3223C6.21062 14.6112 5.81736 14.4221 5.81736 14.4221C5.27298 14.0403 5.8584 14.0482 5.8584 14.0482C6.4609 14.0916 6.77816 14.6821 6.77816 14.6821C7.3133 15.6226 8.18179 15.3507 8.52418 15.1935C8.57803 14.7959 8.73353 14.5245 8.90512 14.3709C7.57243 14.2153 6.17147 13.6878 6.17147 11.3306C6.17147 10.6589 6.40586 10.1102 6.78968 9.67934C6.72739 9.52439 6.52201 8.89869 6.84781 8.05131C6.84781 8.05131 7.35165 7.88597 8.49825 8.6819C8.97686 8.54559 9.49014 8.47724 10.0001 8.47489C10.51 8.47724 11.0236 8.54559 11.5031 8.6819C12.6483 7.88597 13.1515 8.05131 13.1515 8.05131C13.4781 8.89869 13.2726 9.52439 13.2103 9.67934C13.595 10.1102 13.8278 10.6589 13.8278 11.3306C13.8278 13.6934 12.4242 14.2137 11.0881 14.366C11.3033 14.5569 11.4951 14.9313 11.4951 15.5052C11.4951 16.3284 11.4881 16.9909 11.4881 17.1935C11.4881 17.3572 11.5961 17.549 11.9003 17.4886C14.283 16.6742 16 14.3691 16 11.6519C16 8.25424 13.3137 5.5 10.0001 5.5Z"
					fill="#161514"
				/>
			</g>
			<g clipPath={`url(#${clipId})`}>
				<path
					d="M18 6.5C18 3.73858 20.2386 1.5 23 1.5L33 1.5C35.7614 1.5 38 3.73858 38 6.5V16.5C38 19.2614 35.7614 21.5 33 21.5H23C20.2386 21.5 18 19.2614 18 16.5V6.5Z"
					fill="#94C748"
				/>
				<path
					d="M32.8689 11.3016L32.0471 16.3216C31.9935 16.6253 31.7791 16.8039 31.4754 16.8039H24.5081C24.2044 16.8039 23.9901 16.6253 23.9365 16.3216L22.4894 7.37131C22.4358 7.06761 22.5966 6.87109 22.8824 6.87109H33.1011C33.3869 6.87109 33.5477 7.06761 33.4941 7.37131L33.1011 9.72946C33.0475 10.0689 32.851 10.2118 32.5294 10.2118H26.3839C26.2946 10.2118 26.241 10.2654 26.2589 10.3726L26.7412 13.3381C26.7591 13.4096 26.8127 13.4632 26.8842 13.4632H29.0994C29.1708 13.4632 29.2244 13.4096 29.2423 13.3381L29.5817 11.1944C29.6175 10.9264 29.7961 10.8192 30.0462 10.8192H32.458C32.8153 10.8192 32.9224 10.9979 32.8689 11.3016Z"
					fill="#101214"
				/>
			</g>
			<defs>
				<mask id={githubMaskId} maskUnits="userSpaceOnUse" x="0" y="0" width="40" height="23">
					<rect width="40" height="23" fill="white" />
					<rect x="16.5" width="23" height="23" rx="6.5" fill="black" />
				</mask>
				<clipPath id={clipId}>
					<rect width="20" height="20" fill="white" transform="translate(18 1.5)" />
				</clipPath>
			</defs>
		</svg>
	);
};

export const DevDocsChooseRepositoryIllustration = (): React.JSX.Element => (
	<svg
		aria-hidden="true"
		data-testid="dev-docs-choose-repository-illustration"
		fill="none"
		focusable="false"
		height="59"
		viewBox="0 0 74 59"
		width="74"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path d="M57.8899 7 28.0599 58.67l-12.12-7L45.7699 0l12.12 7Z" fill="#6A9A23" />
		<path d="m50.9698 3-12.19 15.06 8.67 7.01 10.44-18.08L50.9698 3Z" fill="#FCA700" />
		<path d="m26.0699 34.1104-10.13 17.55 7.37 4.26 11.47-15.27-8.71-6.54Z" fill="#AF59E1" />
		<path
			d="m34.96 18.5599-14.25 11.53 14.07 10.55-7.64 10.18L0 30.4699l26.96-21.8 8 9.9v-.01Zm38.78 11.41L46.6 50.3299l-7.64-10.18 14.07-10.55-14.25-11.53 8-9.9 26.96 21.8Z"
			fill={token('color.background.neutral.hovered')}
		/>
		<path
			d="m46.7798 8.16016-8 9.90004 8.67 7.01 6.45-11.15-7.12-5.76004Z"
			fill={token('color.icon')}
		/>
		<path
			d="m34.7802 40.6504-8.71-6.54-6.41 11.11 7.48 5.61 7.64-10.18Z"
			fill={token('color.icon')}
		/>
	</svg>
);

export const DevDocsScmProviderPanelIcons = (): React.JSX.Element => {
	const id = useId().replace(/:/g, '');
	const clipId = `devDocsScmProviderPanelIconsClip-${id}`;

	return (
		<svg
			aria-hidden="true"
			fill="none"
			focusable="false"
			height="36"
			viewBox="0 0 64 36"
			width="64"
			xmlns="http://www.w3.org/2000/svg"
		>
			<rect y="1.58496" width="32" height="32" rx="8" fill="#94C748" />
			<path
				d="m23.79 17.2694-1.3149 8.032c-.0857.4859-.4287.7718-.9147.7718H10.4128c-.4859 0-.8289-.2859-.91465-.7718L7.18287 10.981c-.08575-.4859.1715-.8003.62884-.8003H24.1616c.4573 0 .7145.3144.6288.8003l-.6288 3.773c-.0858.5431-.4002.7718-.9147.7718h-9.8328c-.1429 0-.2287.0858-.2001.2573l.7718 4.7448c.0286.1144.1143.2001.2286.2001h3.5444c.1144 0 .2001-.0857.2287-.2001l.5431-3.43c.0571-.4288.343-.6003.7432-.6003h3.8587c.5717 0 .7432.2859.6575.7718Z"
				fill="#101214"
			/>
			<g clipPath={`url(#${clipId})`}>
				<path
					d="M28.8362 11.3009c-.4618-4.39407 2.7259-8.33057 7.12-8.7924L51.8685.83604c4.3941-.46183 8.3306 2.72588 8.7924 7.11995l1.6725 15.91231c.4618 4.3941-2.7259 8.3306-7.12 8.7924l-15.9123 1.6725c-4.3941.4618-8.3306-2.7259-8.7924-7.1199l-1.6725-15.9124Z"
					fill="white"
				/>
				<path
					d="m44.184 7.67665.704-.074c.1691.01181.4051-.0178.5828-.0201.3878-.00388.7756.01403 1.1614.05365 2.4546.25864 4.7268 1.41567 6.3798 3.2485 1.0431 1.1532 1.8012 2.5349 2.2133 4.0343.0699.2539.1299.5104.1798.769.0143.0791.0678.4293.0866.4867l.0778.7412c-.0086.0816.0143.4199.0167.5314.0038.2989-.0053.5979-.0275.896-.1393 1.8411-.7861 3.6076-1.8684 5.1034-.8779 1.217-2.0181 2.2212-3.3362 2.9385-.1968.1069-.4092.2205-.6176.3019-.1613.063-.3936.057-.5348-.0488-.2035-.1523-.1971-.4168-.2192-.6453l-.0529-.5387c-.0643-.6631-.1273-1.34-.2051-2.0007-.0775-.6593-.3182-1.2569-.8528-1.6748.2042-.0389.4631-.1083.6635-.1663.8929-.2583 1.7672-.6617 2.3992-1.3634.8647-.9602 1.0545-2.1781.9938-3.4254-.059-1.2115-.4193-2.1713-1.3354-2.9945.2475-.9105.0307-1.8038-.3752-2.6273-.843-.0722-2.0136.7975-2.6241 1.3074-1.7173-.262-3.3494-.112-4.9742.5247-.7164-.372-2.0169-.9686-2.8396-.7347-.2684.8215-.2146 1.8718.1763 2.6489-.5117.6865-.7801 1.5239-.7627 2.3801.0168 1.2543.4549 2.7489 1.3708 3.6421.958.9345 2.4039 1.2043 3.694 1.1875-.3608.4279-.4678.8499-.4947 1.4016-.2963.1627-.5405.2732-.8774.3433-.7398.1538-1.4025-.0704-1.9119-.6248-.1054-.1147-.1979-.2407-.2999-.3591-.1534-.1747-.3288-.3286-.5218-.4581-.3595-.2371-.7339-.3429-1.1598-.2523-.2941.0625-.3931.2031-.1327.4213.1824.1528.4031.1979.5848.3461.329.2625.5997.6137.8302.9637.0788.1197.1385.2468.21.3674.1554.2618.3774.4776.6435.6255.8105.4514 1.8849.264 2.732.0209.0094.164.0397.3778.0585.5434l.0961.8689c.0153.1346.0699.4232.0524.5442-.0924.6378-.7689.4192-1.1363.3176-.8655-.2391-1.6951-.5932-2.4666-1.0527-2.0878-1.241-3.6515-3.2007-4.398-5.5119-.1144-.3558-.209-.7176-.2832-1.0839-.0325-.1631-.0645-.4259-.1026-.5727l-.0025-.0095-.0752-.7149c.007-.0755-.0138-.3628-.0165-.4606-.0073-.2732-.0031-.5466.0127-.8195.0895-1.5655.5476-3.0878 1.3367-4.4427 1.2301-2.1184 3.1954-3.7112 5.5226-4.47596.3729-.12182.7526-.22227 1.137-.30087.1794-.03621.4197-.05783.5883-.10532Z"
					fill="#1D2022"
				/>
				<path
					d="m36.0084 3.00576 15.9124-1.67246c4.1194-.43297 7.8099 2.55551 8.2429 6.67496l1.6724 15.91234c.433 4.1195-2.5555 7.8099-6.6749 8.2429l-15.9124 1.6724c-4.1194.433-7.8099-2.5555-8.2429-6.6749l-1.6724-15.9124c-.433-4.11941 2.5555-7.80987 6.6749-8.24284Z"
					stroke="#0B120E"
					strokeOpacity=".14"
				/>
			</g>
			<defs>
				<clipPath id={clipId}>
					<path d="M28.8362 11.3009c-.4618-4.39407 2.7259-8.33057 7.12-8.7924L51.8685.83604c4.3941-.46183 8.3306 2.72588 8.7924 7.11995l1.6725 15.91231c.4618 4.3941-2.7259 8.3306-7.12 8.7924l-15.9123 1.6725c-4.3941.4618-8.3306-2.7259-8.7924-7.1199l-1.6725-15.9124Z" />
				</clipPath>
			</defs>
		</svg>
	);
};
